import { Clock, MapPin, MessageCircle, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { SITE, whatsappLink } from "../../data/site";
import { Card } from "../ui/Card";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { StatusLoja } from "../ui/StatusLoja";

const InfoCard = ({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children: ReactNode }) => (
  <Card data-reveal className="group p-6 transition-colors hover:border-primary/40">
    <span className="grid size-12 place-items-center rounded-xl bg-primary-soft text-primary transition-colors group-hover:bg-primary group-hover:text-night">
      <Icon className="size-6" aria-hidden="true" />
    </span>
    <h3 className="mt-4 font-display text-2xl tracking-wide">{title}</h3>
    <div className="mt-2 space-y-1 text-sm text-muted">{children}</div>
  </Card>
);

export const Contato = () => (
  <section id="contato" aria-labelledby="contato-title" className="relative bg-coal py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <SectionHeading
          id="contato-title"
          eyebrow="Contato"
          title="Base"
          highlight="Secreta"
          description="Localize nossa base no radar ou entre em contato pela linha direta."
        />
      </Reveal>

      <div className="mt-12 grid gap-6 lg:grid-cols-[1fr_1.35fr]">
        <Reveal stagger={0.12} className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
          <InfoCard icon={MapPin} title="Coordenadas">
            <p>{SITE.address.street}</p>
            <p>{SITE.address.city}</p>
            <p className="pt-1 text-xs font-semibold uppercase tracking-wider text-primary">{SITE.address.label}</p>
          </InfoCard>

          <InfoCard icon={Clock} title="Radar de Operação">
            {SITE.hours.slice(0, 2).map((h) => (
              <p key={h.days}>
                {h.days}: {h.time}
              </p>
            ))}
            <StatusLoja className="mt-3" />
          </InfoCard>

          <InfoCard icon={MessageCircle} title="Linha Direta">
            <p>{SITE.phone}</p>
            <p className="break-all">{SITE.email}</p>
            <a
              href={whatsappLink("Olá! Vim pelo site do Ninja Burger. 🍔")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-success px-4 py-2 text-sm font-semibold text-night transition-transform hover:scale-[1.03]"
            >
              <FaWhatsapp className="size-4" aria-hidden="true" /> Chamar no Whats
            </a>
          </InfoCard>
        </Reveal>

        <Reveal className="relative min-h-[380px] overflow-hidden rounded-card border border-line">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 rounded-card ring-1 ring-inset ring-primary/20" />
          <iframe
            title="Mapa com a localização do Ninja Burger em Guarulhos"
            src={SITE.mapEmbedUrl}
            className="absolute inset-0 size-full border-0 [filter:invert(92%)_hue-rotate(180deg)_saturate(0.6)]"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Reveal>
      </div>
    </div>
  </section>
);
